import { v4 as uuidv4 } from 'uuid';
import { normalizeHexColor } from './richText.js';
import { normalizeStoredImageItem, revokeLocalImageUrl } from './localImage.js';
import { LOCAL_ASSET } from './localAssets.js';
import { getMediaItemUrl } from './qiniuMedia.js';

export const IMAGE_CARD_TYPE = 'image-card';
export const IMAGE_CARD_DEFAULT_HEIGHT = 150;
export const IMAGE_CARD_COL_COUNT = 2;
export const IMAGE_CARD_COL_GAP = 12;
export const IMAGE_CARD_H5_CONTENT_WIDTH = 345;

const DEFAULT_SUBTITLE_COLOR = '#999999';
const DEFAULT_TITLE_COLOR = '#333333';

const CARD_PRESETS = [
	{
		subtitle: '内容变现 轻量起步',
		title: 'AI资料包项目实操',
	},
	{
		subtitle: '搜索获客 长期流量',
		title: '长尾词获客项目',
	},
];

export function createDefaultGalleryImages(count = 4, baseIndex = 0) {
	const images = [];
	const gallery = LOCAL_ASSET.imgCardGallery;
	for (let i = 0; i < count; i += 1) {
		images.push({
			id: `image-card-gallery-${uuidv4()}`,
			filename: gallery[(baseIndex + i) % gallery.length]?.split('/').pop() || 'demo.jpg',
			previewUrl: gallery[(baseIndex + i) % gallery.length] || gallery[0],
			url: '',
			local: false,
		});
	}
	return images;
}

export function createDefaultImageCardItem(preset = {}, index = 0) {
	return {
		id: uuidv4(),
		subtitle: preset.subtitle || ' ',
		title: preset.title || ' ',
		subtitleColor: DEFAULT_SUBTITLE_COLOR,
		titleColor: DEFAULT_TITLE_COLOR,
		showImageCount: true,
		items: createDefaultGalleryImages(2, index * 2),
	};
}

export function createDefaultImageCardItems(count = 2) {
	const items = [];
	for (let i = 0; i < count; i += 1) {
		items.push(createDefaultImageCardItem(CARD_PRESETS[i] || {}, i));
	}
	return items;
}

export function getImageCardDefaults() {
	return normalizeImageCardData({
		items: createDefaultImageCardItems(2),
	});
}

export function isImageCardBlock(blockType) {
	return blockType === IMAGE_CARD_TYPE;
}

export function getImageCardImageUrl(image) {
	if (!image) return '';
	return getMediaItemUrl(image);
}

function normalizeCardImage(image, fallback) {
	if (!image?.previewUrl && !image?.url) {
		return fallback ? { ...fallback } : null;
	}
	return normalizeStoredImageItem(image, fallback);
}

function normalizeGalleryImages(images, fallbackImages = []) {
	if (!Array.isArray(images) || !images.length) {
		return fallbackImages.map(img => ({ ...img }));
	}
	return images.map((image, index) => {
		const fallback = fallbackImages[index] || fallbackImages[0];
		return normalizeCardImage(image, fallback);
	});
}

export function normalizeImageCardItem(item = {}, index = 0) {
	const fallback = createDefaultImageCardItem({}, index);
	return {
		id: item.id || uuidv4(),
		subtitle: item.subtitle ?? fallback.subtitle,
		title: item.title ?? fallback.title,
		subtitleColor: normalizeHexColor(item.subtitleColor || fallback.subtitleColor),
		titleColor: normalizeHexColor(item.titleColor || fallback.titleColor),
		showImageCount: item.showImageCount !== false,
		items: normalizeGalleryImages(item.items ?? item.images, fallback.items),
	};
}

export function normalizeImageCardItems(items = []) {
	if (!items.length) {
		return createDefaultImageCardItems(2);
	}
	return items.map((item, index) => normalizeImageCardItem(item, index));
}

export function normalizeImageCardData(data = {}) {
	const height = Number(data.imageHeight);
	return {
		items: normalizeImageCardItems(data.items),
		imageHeight: Number.isFinite(height) && height > 0
			? height
			: IMAGE_CARD_DEFAULT_HEIGHT,
		borderRadius: Number.isFinite(Number(data.borderRadius))
			? Math.max(0, Number(data.borderRadius))
			: 8,
	};
}

export function getImageCardRefColumnWidth(contentWidth = IMAGE_CARD_H5_CONTENT_WIDTH) {
	const gaps = IMAGE_CARD_COL_GAP * (IMAGE_CARD_COL_COUNT - 1);
	return (contentWidth - gaps) / IMAGE_CARD_COL_COUNT;
}

export function getImageCardCoverAspectRatio(imageHeight = IMAGE_CARD_DEFAULT_HEIGHT) {
	const colWidth = getImageCardRefColumnWidth();
	const height = Number(imageHeight) || IMAGE_CARD_DEFAULT_HEIGHT;
	return `${colWidth} / ${height}`;
}

export function getImageCardBlockStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizeImageCardData(data);
	const pxRatio = h5 ? 50 : 1;
	const radius = normalized.borderRadius;

	return {
		coverAspectRatio: getImageCardCoverAspectRatio(normalized.imageHeight),
		borderRadius: h5 ? `${radius / pxRatio}rem` : `${radius}px`,
	};
}

export function getImageCardCoverImage(item) {
	const images = item?.items ?? item?.images ?? [];
	return images[0] || null;
}

export function replaceImageCardGalleryImage(currentImage, file, createLocalImageItem) {
	if (currentImage?.local) {
		revokeLocalImageUrl(currentImage.previewUrl);
	}
	return createLocalImageItem(file);
}
